import { useEffect, useState } from 'react';
import type { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { ProcessingNotice } from '@/components/ui/processing-notice';
import {
  downloadGatewayReport,
  fetchGateways,
  GatewayInputs,
  GatewaySummary,
  reconcileGateways,
} from '../../api/reconToolsApi';
import { downloadBlob } from '../../api/files';
import { Field, inputClass, RecordTable, Section, Stat } from './reconUi';

const STEPS = [
  'Upload the ERP / internal sales report. It must say which gateway processed each order.',
  'Add one settlement file for each gateway you want reconciled. Only the gateways you add are checked.',
  'Optionally add the bank statement to confirm each payout actually landed.',
  'Click Run Reconciliation, then download the Excel report.',
];

const HEADLINE_LABELS: Record<string, string> = {
  gateway_matched: 'Gateway matched',
  gateway_unmatched: 'Gateway unmatched',
  bank_received: 'Bank received',
  bank_not_received: 'Bank not received',
  exceptions: 'Exceptions',
  cod_orders: 'COD orders',
  cod_issues: 'COD issues',
};
const BAD_HEADLINES = new Set(['gateway_unmatched', 'bank_not_received', 'exceptions', 'cod_issues']);

interface GatewayRow {
  id: number;
  gateway: string;
  file: File | null;
}

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const STATUS_COLS = ['recon_status', 'status', 'bank_status', 'cod_status', 'priority', 'gap_flag'];

function PaygwReconPanel({ mission, onBack }: PanelProps) {
  const [gateways, setGateways] = useState<string[]>([]);
  const [erp, setErp] = useState<File[]>([]);
  const [bank, setBank] = useState<File[]>([]);
  const [asOf, setAsOf] = useState('');
  const [rows, setRows] = useState<GatewayRow[]>([{ id: 1, gateway: '', file: null }]);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<GatewaySummary | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchGateways()
      .then(setGateways)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the gateway list.'));
  }, []);

  const updateRow = (id: number, patch: Partial<GatewayRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const complete = rows.filter((r): r is GatewayRow & { file: File } => !!r.gateway && !!r.file);
  const ready = erp.length > 0 && complete.length > 0;

  const inputs = (): GatewayInputs => ({
    erp: erp[0],
    bank: bank[0],
    settlements: complete.map((r) => ({ gateway: r.gateway, file: r.file })),
    as_of: asOf,
  });

  const fail = (message: string) => {
    setError(message);
    setStatus('error');
  };

  const handleRun = async () => {
    if (!ready) {
      fail('Upload the ERP report and add at least one gateway with its settlement file.');
      return;
    }
    setStatus('loading');
    setError(null);
    setSummary(null);
    try {
      setSummary(await reconcileGateways(inputs()));
      setStatus('success');
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Reconciliation failed.');
    }
  };

  const handleDownload = async () => {
    if (!ready) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadGatewayReport(inputs());
      downloadBlob(blob, filename);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Failed to build the report.');
    } finally {
      setDownloading(false);
    }
  };

  const fileProps = { accept: mission.accept, hint: mission.maxSizeLabel, accentColor: mission.color };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide backLabel="Reconciliation Tools">
      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop label="ERP / internal sales report" required {...fileProps} files={erp} onFiles={setErp} />
        <FileDrop label="Bank statement (optional)" {...fileProps} files={bank} onFiles={setBank} />
      </div>

      <div className="mt-2 rounded-lg border border-border bg-muted p-4">
        <p className="text-sm font-semibold text-foreground">Gateway settlement reports</p>
        <p className="mb-3 text-xs text-muted-foreground">
          Only the gateways you add here are reconciled; ERP orders and bank credits for any other gateway are left out.
        </p>
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="flex flex-wrap items-center gap-2">
              <select
                className={`${inputClass} w-40`}
                value={row.gateway}
                onChange={(e) => updateRow(row.id, { gateway: e.target.value })}
              >
                <option value="">Select gateway…</option>
                {gateways.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              <input
                type="file"
                accept={mission.accept}
                className="min-w-0 flex-1 text-sm"
                onChange={(e) => updateRow(row.id, { file: e.target.files?.[0] ?? null })}
              />
              {rows.length > 1 && (
                <button
                  type="button"
                  className="px-2 text-lg text-muted-foreground hover:text-foreground"
                  title="Remove"
                  onClick={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setRows((prev) => [...prev, { id: Math.max(0, ...prev.map((r) => r.id)) + 1, gateway: '', file: null }])}
        >
          + Add gateway file
        </Button>
      </div>

      <div className="mt-3 max-w-xs">
        <Field label="As-of date (optional)">
          <input className={inputClass} type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </Field>
      </div>

      <div className="mt-4 flex gap-3">
        <Button
          onClick={handleRun}
          disabled={status === 'loading'}
          style={{ backgroundColor: mission.color }}
          className="flex-1 text-white hover:opacity-90"
        >
          {status === 'loading' ? 'Reconciling…' : 'Run Reconciliation'}
        </Button>
        <Button variant="outline" onClick={handleDownload} disabled={!ready || downloading} className="flex-1">
          {downloading ? 'Preparing report…' : 'Download Excel Report'}
        </Button>
      </div>
      {status === 'loading' && <ProcessingNotice label="Reconciling…" />}
      {error && <ErrorNotice message={error} />}
      {status === 'idle' && !error && (
        <InfoNotice>📁 Upload the ERP report, then add each gateway's settlement file.</InfoNotice>
      )}

      {status === 'success' && summary && (
        <div className="mt-4 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Object.entries(summary.headline).map(([key, value]) => (
              <Stat key={key} label={HEADLINE_LABELS[key] ?? key} value={value} tone={BAD_HEADLINES.has(key) && value > 0 ? 'bad' : undefined} />
            ))}
          </div>

          {summary.unmapped.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              ⚠️ ERP rows for gateways that were not reconciled:{' '}
              {summary.unmapped.map((u) => `${u.source} (${u.rows} rows, ${u.amount})`).join(', ')}
            </div>
          )}

          {([
            ['Per-gateway summary', summary.per_source],
            ['Exceptions', summary.exceptions_preview],
            ['Gateway unmatched', summary.gateway_unmatched_preview],
            ['Bank payment not received', summary.bank_not_received_preview],
            ['COD reconciliation', summary.cod_reconciliation_preview],
            ['Gateway matched', summary.gateway_matched_preview],
            ['Bank payment received', summary.bank_received_preview],
          ] as const).map(([title, data]) =>
            data.length > 0 ? (
              <Section key={title} title={`${title} (${data.length})`}>
                <RecordTable rows={data} statusCols={STATUS_COLS} />
              </Section>
            ) : null
          )}
          <p className="text-xs text-muted-foreground">Tables show up to 200 rows; the Excel report has every row.</p>
        </div>
      )}
    </MissionShell>
  );
}

export default PaygwReconPanel;
